import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { numStr, parseNum } from "@/lib/format";
import { buildSources, ingredientCalc, recipeTotals, sourceKey } from "@/lib/recipeCalc";
import { toFormIngredient } from "@/components/recipes/IngredientsEditor";

const EMPTY = {
  name: "", menu_category: "", menu_description: "", prep_time_min: "", yield_portions: "1", photo_path: null, plating_notes: "", plating_photo_path: null, sale_price: "",
  is_base: false, yield_qty: "", yield_unit: "kg", own_cmv_target: "", sales_code: "",
};
const toSteps = (arr) => (arr || []).map((text) => ({ key: crypto.randomUUID(), text }));

function fromServer(d) {
  return {
    f: {
      ...EMPTY, ...d, prep_time_min: d.prep_time_min ? String(d.prep_time_min) : "", yield_portions: numStr(d.yield_portions) || "1", sale_price: numStr(d.sale_price),
      is_base: !!d.is_base, yield_qty: numStr(d.yield_qty), yield_unit: d.yield_unit || "kg", own_cmv_target: numStr(d.own_cmv_target), sales_code: d.sales_code || "",
    },
    ingredients: d.ingredients.map(toFormIngredient),
    steps: toSteps(d.steps),
  };
}

function toBody(f, ingredients, steps) {
  const yq = parseNum(f.yield_qty);
  return {
    ...f,
    prep_time_min: f.prep_time_min ? parseInt(f.prep_time_min, 10) : null,
    yield_portions: parseNum(f.yield_portions) || 1,
    sale_price: parseNum(f.sale_price),
    yield_qty: yq || null,
    yield_unit: yq ? f.yield_unit : null,
    own_cmv_target: parseNum(f.own_cmv_target) || null,
    ingredients: ingredients.map((i) => ({ kind: i.kind || "product", product_id: i.product_id, recipe_id: i.recipe_id, unit: i.unit, qty_gross: parseNum(i.qty_gross), loss_pct: Math.min(parseNum(i.loss_pct), 99) })),
    steps: steps.map((s) => s.text),
  };
}

export function copyForm(f, ingredients, steps) {
  const { id: _id, sales_aliases: _a, photo_path: _p, plating_photo_path: _pp, ...rest } = f;
  return {
    f: { ...rest, name: `${f.name} (cópia)`, sales_code: "", photo_path: null, plating_photo_path: null },
    ingredients: ingredients.map((i) => ({ ...i, key: crypto.randomUUID() })),
    steps: steps.map((s) => s.text),
  };
}

export default function useRecipeForm(id) {
  const loc = useLocation();
  const copy = !id ? loc.state?.copy : null;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["recipe", id], queryFn: () => api(`/recipes/${id}`), enabled: !!id, retry: false });
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const { data: recipes = [] } = useQuery({ queryKey: ["recipes"], queryFn: () => api("/recipes") });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api("/settings") });
  const [f, setF] = useState(copy?.f || EMPTY);
  const [ingredients, setIngredients] = useState(copy?.ingredients || []);
  const [steps, setSteps] = useState(() => toSteps(copy?.steps?.length ? copy.steps : [""]));
  const snap = (a, b, c) => JSON.stringify([a, b.map(({ key, ...i }) => i), c.map((s) => s.text ?? s)]);
  const [base, setBase] = useState(() => snap(EMPTY, [], [""]));
  const reset = (nf, ni, ns) => { setF(nf); setIngredients(ni); setSteps(ns); setBase(snap(nf, ni, ns)); };
  useEffect(() => {
    if (!data) return;
    const s = fromServer(data);
    reset(s.f, s.ingredients, s.steps.length ? s.steps : toSteps([""]));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const sources = useMemo(() => buildSources(products, recipes, id), [products, recipes, id]);
  const unitTarget = settings?.cmv_target ?? 30;
  const target = parseNum(f.own_cmv_target) || unitTarget;
  const totals = useMemo(() => {
    const costs = ingredients.map((i) => ingredientCalc({ ...i, qty_gross: parseNum(i.qty_gross), loss_pct: parseNum(i.loss_pct) }, sources[sourceKey(i)]).cost);
    return recipeTotals(costs, parseNum(f.yield_portions), parseNum(f.sale_price), target);
  }, [ingredients, sources, f.yield_portions, f.sale_price, target]);

  const save = useMutation({
    mutationFn: () => api(id ? `/recipes/${id}` : "/recipes", { method: id ? "PUT" : "POST", body: toBody(f, ingredients, steps) }),
    onSuccess: (d, andNew) => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.setQueryData(["recipe", d.id], d);
      if (andNew) {
        toast.success("Ficha técnica criada · pronta para a próxima");
        reset({ ...EMPTY, menu_category: f.menu_category }, [], toSteps([""]));
        if (copy) nav("/recipes/new", { replace: true, state: null });
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      toast.success(id ? "Ficha técnica atualizada" : "Ficha técnica criada");
      if (!id) nav(`/recipes/${d.id}`, { replace: true });
      else setBase(snap(f, ingredients, steps));
    },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: () => api(`/recipes/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recipes"] }); toast.success("Ficha excluída"); nav("/recipes", { replace: true }); },
    onError: (e) => toast.error(e.message),
  });
  const submit = (andNew = false) => {
    if (!f.name.trim()) return toast.error("Informe o nome do prato");
    if (parseNum(f.yield_portions) <= 0) return toast.error("O rendimento deve ser maior que zero");
    const t = parseNum(f.own_cmv_target);
    if (t < 0 || t >= 100) return toast.error("A meta de CMV deve estar entre 0 e 100%");
    save.mutate(andNew);
  };
  return {
    f, setF, ingredients, setIngredients, steps, setSteps, sources, totals, submit, save, del, data,
    dirty: snap(f, ingredients, steps) !== base,
    target, unitTarget, loading: !!id && isLoading, error: !!id && isError ? error.message : null,
  };
}
